import Box from "@mui/material/Box";
import GlobalStyles from "@mui/material/GlobalStyles";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { type ReactNode, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useAssetDetailsContext } from "../../context/assetDetailsContext";
import { AssetAttachmentsGrid } from "./AssetAtachments/AssetAttachmentsGrid";
import { AssetLOTOGrid } from "./AssetLOTO/AssetLOTOGrid";
import { AssetPartsGrid } from "./AssetPartsGrid/AssetPartsGrid";
import { AssetTextGrid } from "./AssetText/AssetTextGrid";
import { Paper } from "@mui/material";
import { AssetQCBDTable } from "./AssetQCBD/AssetQCBD";
import { AssetDocumentsGrid } from "./AssetDocumentsGrid/AssetDocumentsGrid";
import { AssemblyGrid } from "./AssemblyGrid/AssemblyGrid";

function a11yProps(index: number) {
	return {
		id: `asset-tab-${index}`,
		"aria-controls": `asset-tabpanel-${index}`,
	};
}

type TabPanelProps = {
	children?: ReactNode;
	index: number;
	value: number;
};

function TabPanel(props: TabPanelProps) {
	const { children, value, index, ...other } = props;

	return (
		<div
			role="tabpanel"
			hidden={value !== index}
			id={`simple-tabpanel-${index}`}
			aria-labelledby={`simple-tab-${index}`}
			{...other}
			style={{ height: "100%" }}
		>
			{value === index && (
				<Box sx={{ padding: 0, margin: 0 }} style={{ height: "100%" }}>
					{children}
				</Box>
			)}
		</div>
	);
}

const RESOURCES: Resource[] = [
	{
		ResourceName: "Parts",
		Value: 0,
	},
	{
		ResourceName: "Assemblies",
		Value: 1,
	},
	{
		ResourceName: "Documents",
		Value: 2,
	},
	{
		ResourceName: "Attachments",
		Value: 3,
	},
	{
		ResourceName: "LOTO",
		Value: 4,
	},
	{
		ResourceName: "Text",
		Value: 5,
	},
	{
		ResourceName: "QCBD",
		Value: 6,
	},
];

type Resource = {
	ResourceName: string;
	Value: number;
};

type Props = {
	searchStatistics: Function;
};

const clearClipboard = () => {
	localStorage.removeItem("attachmentCopied");
	localStorage.removeItem("documentCopied");
};

export default function AssetDetailsTabs({ searchStatistics }: Props) {
	const theme = useTheme();
	const [searchParams, setSearchParams] = useSearchParams();
	const resource = searchParams.get("resource") ?? "";
	const isWideScreen = useMediaQuery("(min-width:1300px)");
	const {
		state: { gridValue },
		actions: { setGridValue, setCurrentGridName },
	} = useAssetDetailsContext();

	const handleChangeResource = (e: any, newValue: number) => {
		const resourceName: string = (e.target?.innerText).toLowerCase();
		clearClipboard();
		searchParams.set("resource", resourceName);
		setCurrentGridName(resourceName);
		setGridValue(newValue);
		setSearchParams(searchParams, { replace: true });
	};

	useEffect(() => {
		const resourceCurrentData = RESOURCES.find(
			(r) => r.ResourceName.toLocaleLowerCase() == resource,
		);
		setGridValue(resourceCurrentData?.Value);
		setCurrentGridName(resource);
		clearClipboard();
	}, [resource]);

	return (
		<Paper
			elevation={1}
			sx={{
				height: "100%",
				width: "100%",
				padding: "10px",
				display: "flex",
				flexDirection: "column",
				boxSizing: "border-box",
			}}
		>
			{/* App-wide style for the resource grids' context menus: a disabled item (e.g. "Remove"
			    when the resource belongs to another asset) drops the not-allowed cursor and shows a
			    prompt, MUI-chip-styled tooltip instead of the plain default box. Rendered here (always
			    mounted) so it applies on every tab, not only the one whose grid injected it. */}
			<GlobalStyles
				styles={{
					".ag-menu-option-disabled, .ag-menu-option-disabled *": {
						cursor: "default !important",
					},
					".ag-tooltip": {
						backgroundColor: "rgba(97, 97, 97, 0.92) !important",
						color: "#fff !important",
						border: "none !important",
						borderRadius: "4px !important",
						padding: "4px 8px !important",
						fontSize: "0.6875rem !important",
						fontWeight: 500,
						fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif !important",
						boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15) !important",
						maxWidth: "300px",
						whiteSpace: "normal",
					},
				}}
			/>
			<Box sx={{ flexShrink: 0 }}>
				<Box
					id="tabs-grid"
					sx={{
						borderBottom: 1,
						borderColor: "divider",
					}}
				>
					<Tabs
						value={gridValue}
						onChange={(e, newValue: number) =>
							handleChangeResource(e, newValue)
						}
						variant={isWideScreen ? "fullWidth" : "scrollable"}
						{...(isWideScreen ? {} : { scrollButtons: "auto" })}
						aria-label="asset tabs"
						sx={{
							minHeight: 35,
							"& .MuiTabs-indicator": {
								display: "none",
							},
							"& .MuiTabs-flexContainer": {
								borderBottom: "1px solid #e0e0e0",
							},
							"& .MuiTabs-scrollButtons": {
								color: theme.palette.primary.main,
								"&.Mui-disabled": {
									opacity: 0.3,
								},
							},
						}}
					>
						{RESOURCES.map((resource) => (
							<Tab
								key={resource.Value}
								label={resource.ResourceName}
								{...a11yProps(resource.Value)}
								sx={{
									textTransform: "none",
									fontWeight: "bold",
									fontSize: "12px",
									minHeight: 35,
									minWidth: "120px",
									padding: "6px 16px",
									color: "#666666",
									transition: "all 0.2s ease-in-out",
									"&:hover": {
										backgroundColor: `${theme.palette.primary.main}15`,
										color: theme.palette.primary.main,
									},
									"&.Mui-selected": {
										color: "#ffffff",
										backgroundColor: theme.palette.primary.main,
										borderRadius: "4px 4px 0 0",
										borderBottom: `3px solid ${theme.palette.primary.main}`,
										"&:hover": {
											backgroundColor: theme.palette.primary.main,
											color: "#ffffff",
										},
									},
								}}
							/>
						))}
					</Tabs>
				</Box>
			</Box>
			<Box sx={{ flex: 1, minHeight: 0, height: "100%" }}>
				<TabPanel value={gridValue} index={0}>
					<AssetPartsGrid searchStatistics={searchStatistics} />
				</TabPanel>
				<TabPanel value={gridValue} index={1}>
					<AssemblyGrid searchStatistics={searchStatistics} />
				</TabPanel>
				<TabPanel value={gridValue} index={2}>
					<AssetDocumentsGrid searchStatistics={searchStatistics} />
				</TabPanel>
				<TabPanel value={gridValue} index={3}>
					<AssetAttachmentsGrid searchStatistics={searchStatistics} />
				</TabPanel>
				<TabPanel value={gridValue} index={4}>
					<AssetLOTOGrid searchStatistics={searchStatistics} />
				</TabPanel>
				<TabPanel value={gridValue} index={5}>
					<AssetTextGrid />
				</TabPanel>
				<TabPanel value={gridValue} index={6}>
					<AssetQCBDTable />
				</TabPanel>
			</Box>
		</Paper>
	);
}
